import { useEffect, useRef } from 'react'
import {
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  BackwardIcon,
} from '@heroicons/react/20/solid'
import { useAudioStore } from './TranscriptEditor/useAudioStore'
import { Dropdown } from './Common/Dropdown'
import VolumeControl from './TranscriptEditor/VolumeControl'

const AudioPlayer = () => {
  const {
    audioRef,
    setAudioTime,
    audioUrl,
    fastForward,
    rewind,
    isPlaying,
    progress,
    togglePlay,
    duration,
    cleanupAudio,
  } = useAudioStore()

  const seekBarRef = useRef<HTMLDivElement>(null)

  const handleSeek = (e) => {
    if (!audioRef || !seekBarRef || !seekBarRef.current) return

    const { clientX } = e
    const { left, width } = seekBarRef.current.getBoundingClientRect()
    const clickPositionInPixels = clientX - left
    const clickPositionInPercentage = (clickPositionInPixels / width) * 100
    const newProgress = (duration * clickPositionInPercentage) / 100
    setAudioTime(newProgress)
  }

  useEffect(() => {
    return () => {
      cleanupAudio()
    }
  }, [])

  if (!audioUrl) return null

  return (
    <div className="w-full">
      <audio src={audioUrl} />
      <div className="flex items-end justify-between">
        <div className="flex flex-1 items-center gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => rewind(5)}
              className="rounded-full px-2 py-2 text-slate-400 hover:text-slate-600"
            >
              <BackwardIcon className="h-4 w-4" />
            </button>
            <button
              onClick={togglePlay}
              className={
                `rounded-full px-2 py-2 text-white` +
                (isPlaying
                  ? ' bg-slate-500 hover:bg-slate-600'
                  : ' bg-blue-500 hover:bg-blue-600')
              }
            >
              {isPlaying ? (
                <PauseIcon className="h-4 w-4" />
              ) : (
                <PlayIcon className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={() => fastForward(5)}
              className="rounded-full px-2 py-2 text-slate-400 hover:text-slate-600"
            >
              <ForwardIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="flex w-full max-w-xs items-center gap-2">
            <span className="text-xs font-medium text-slate-500">
              {Math.floor(progress / 60)}:
              {Math.floor(progress % 60)
                .toString()
                .padStart(2, '0')}
            </span>
            <div className="flex w-full flex-col-reverse items-center justify-between">
              <div
                ref={seekBarRef}
                onClick={handleSeek}
                className="h-1.5 w-full cursor-pointer rounded-full bg-slate-200"
              >
                <div
                  className="h-2 rounded-full bg-toasty-blue-500"
                  style={{ width: `${(progress / duration) * 100}%` }}
                ></div>
              </div>
            </div>
            <span className="text-xs font-medium text-slate-500">
              {Math.floor(duration / 60)}:
              {Math.floor(duration % 60)
                .toString()
                .padStart(2, '0')}
            </span>
            <VolumeControl />
          </div>
        </div>
      </div>
    </div>
  )
}

export default AudioPlayer
